import type { DealFormDataWithTargeting, DealMetaValue, GroupDeal } from '@openx/types/deal';
import { mixed, object, type TestContext as YupTestContext } from 'yup';
import { validateFormatConsistency } from './validateFormatConsistency';

interface ValidationStackElement {
  value: Record<string, unknown> | DealFormDataWithTargeting;
}

export interface StrictValidationContext extends Omit<YupTestContext, 'from'> {
  from: ValidationStackElement[];
}

const extractFormat = (deal: GroupDeal): string | undefined =>
  deal?.targetingWithUrlTargeting?.rendering_context?.ad_placement?.val?.toUpperCase();

export const getRootFormValueFromContext = (
  context: StrictValidationContext | YupTestContext
): DealFormDataWithTargeting | undefined => {
  if ('from' in context && Array.isArray(context.from)) {
    const stack = context.from as ValidationStackElement[];
    const rootEntry = stack[stack.length - 1];

    if (rootEntry?.value && typeof rootEntry.value === 'object' && '__meta' in rootEntry.value) {
      return rootEntry.value as DealFormDataWithTargeting;
    }
  }
  return undefined;
};

export const getRboGroupFormat = (
  meta: DealMetaValue | undefined,
  rootForm: DealFormDataWithTargeting | undefined
): string | undefined => {
  const { group_deal: groupDeal } = meta || {};
  if (!Array.isArray(groupDeal) || groupDeal?.length === 1) {
    return undefined;
  }

  const referenceDeal = groupDeal.find(deal => deal?.uid !== rootForm?.uid) ?? groupDeal[0];

  return extractFormat(referenceDeal);
};

export const rboMetaSchema = object()
  .shape({
    group_deal: mixed().nullable(),
    group_rbo_config: mixed().nullable(),
  })
  .test({
    name: 'rboGroupFormatValidation',
    test: function (value) {
      const meta = value as DealMetaValue;
      const rootForm = getRootFormValueFromContext(this);
      const groupFormat = getRboGroupFormat(meta, rootForm);

      return validateFormatConsistency(groupFormat, rootForm, this, `rboGroupValidationAlert:${groupFormat}`);
    },
  });
